import {
  APIPersonalAutoApplicationFormDriver,
  APIBinderData,
  APIInsuranceClaim,
  APIInsuranceClaimType,
  APIInsuranceOtherLoss,
  APIInsuranceRecord,
  APIQuoteClaim,
  APIQuoteClaimPayout,
} from 'types/api.types';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import {
  formatFault,
  formatPayout,
  getInsuranceSourceLabel,
  processValidation,
} from '../utils/helpers';

export const processClaimsData = (
  binderReportData: APIBinderData,
  remarks: Remark[] = [],
  validation?: Validation,
): BinderSectionData => {
  const applicationData = binderReportData.application_data;
  const quoteData = binderReportData.quote_data;
  const autoPlusDataArray = binderReportData.insurance_data?.data || [];
  const drivers = applicationData?.drivers || [];
  const insuranceSource = binderReportData.insurance_data?.source;

  const driverClaimsData = drivers.map(
    (driver: APIPersonalAutoApplicationFormDriver, index: number) => {
      const autoplusClaims: any[] = [];

      autoPlusDataArray.forEach((autoPlusData: APIInsuranceRecord) => {
        const claims =
          autoPlusData?.claims?.filter((claim) => {
            const claimLicenseNumber = claim?.driver?.license_number;
            const driverLicenseNumber = driver?.license;

            const licenseMatch =
              claimLicenseNumber &&
              driverLicenseNumber &&
              claimLicenseNumber === driverLicenseNumber;

            return licenseMatch;
          }) || [];

        // Add the filtered claims from this AutoPlus record to the overall array
        autoplusClaims.push(...claims);
      });

      const quoteClaims = quoteData?.drivers?.[index]?.claims || [];

      // Get other losses data from all insurance_data records
      const autoplusOtherLosses: any[] = [];

      // Process other losses from each AutoPlus record
      autoPlusDataArray.forEach((autoPlusData) => {
        const losses =
          autoPlusData?.other_losses?.filter(
            (loss) =>
              loss?.driver === driver?.name || loss?.driver_index === index,
          ) || [];

        // Add the filtered losses from this AutoPlus record to the overall array
        autoplusOtherLosses.push(...losses);
      });

      // Format quote payout (object with multiple values)
      const formatQuotePayout = (payouts: APIQuoteClaimPayout): string => {
        if (!payouts || typeof payouts !== 'object') return '$0';

        const payoutValues = [];
        if (payouts.ab && payouts.ab !== null)
          payoutValues.push(`AB: ${formatPayout(payouts.ab)}`);
        if (payouts.coll && payouts.coll !== null)
          payoutValues.push(`Coll: ${formatPayout(payouts.coll)}`);
        if (payouts['tp/bi'] && payouts['tp/bi'] !== null)
          payoutValues.push(`TP/BI: ${formatPayout(payouts['tp/bi'])}`);
        if (payouts['tp/pd'] && payouts['tp/pd'] !== null)
          payoutValues.push(`TP/PD: ${formatPayout(payouts['tp/pd'])}`);
        if (payouts.other_pd && payouts.other_pd !== null)
          payoutValues.push(`Other PD: ${formatPayout(payouts.other_pd)}`);

        return payoutValues.length > 0 ? payoutValues.join(', ') : '$0';
      };

      // Process AutoPlus Claims
      const processedAutoplusClaims = autoplusClaims.map(
        (claim: APIInsuranceClaim) => ({
          date: claim?.loss_date || '',
          driver:
            claim?.driver?.['1st'] || driver?.name || `Driver ${index + 1}`,
          type:
            claim?.types
              ?.map((t: APIInsuranceClaimType) => {
                return t.code_type;
              })
              .join(', ') || 'Unknown',
          fault: formatFault(claim?.driver?.fault),
          payout: formatPayout(claim?.expenses_on_the_claim),
          vehicle: claim?.vehicle || 'Unknown Vehicle',
          source: claim?.source || 'Unknown',
        }),
      );

      // Process Quote Claims
      const processedQuoteClaims = quoteClaims.map((claim: APIQuoteClaim) => ({
        date: claim?.date || '',
        driver: driver?.name || `Driver ${index + 1}`,
        type: claim?.description || 'Unknown',
        fault:
          claim?.charge === 'Yes'
            ? 'At Fault'
            : claim?.charge === 'No'
            ? 'Not At Fault'
            : 'N/A',
        payout: formatQuotePayout(claim?.payouts),
      }));

      // Process AutoPlus Other Losses
      const processedAutoplusOtherLosses = autoplusOtherLosses.map(
        (loss: APIInsuranceOtherLoss) => ({
          date: loss?.date || loss?.loss_date || '',
          driver: loss?.driver || driver?.name || `Driver ${index + 1}`,
          type: loss?.type || loss?.loss_type || 'Unknown',
          fault: formatFault(loss?.fault || loss?.at_fault),
          payout: formatPayout(
            loss?.payout || loss?.amount || loss?.settlement,
          ),
        }),
      );

      const finalAutoplusClaims = processedAutoplusClaims;
      const finalQuoteClaims = processedQuoteClaims;
      // Other losses come from insurance_data only
      const finalOtherLosses = processedAutoplusOtherLosses;

      return {
        driverIndex: index + 1,
        driverName: driver?.name || `Driver ${index + 1}`,
        autoplusClaims: finalAutoplusClaims,
        quoteClaims: finalQuoteClaims,
        otherLosses: finalOtherLosses,
        totalClaims: finalAutoplusClaims.length + finalQuoteClaims.length,
        totalLosses: finalOtherLosses.length,
        hasClaimsHistory:
          finalAutoplusClaims.length > 0 ||
          finalQuoteClaims.length > 0 ||
          finalOtherLosses.length > 0,
      };
    },
  );

  const validationData = processValidation(validation);

  return {
    title: 'Claims',
    description:
      'Review claims history across multiple data sources for each driver.',
    isValid: validationData.isValid,
    message: validationData.message,
    data: {
      insuranceSource: getInsuranceSourceLabel(insuranceSource),
      drivers: driverClaimsData,
      remarks: remarks.length > 0 ? remarks[0].text : undefined,
    },
  };
};
